import {useState, useEffect, useMemo} from "react";
import {useReactTable, getCoreRowModel, getSortedRowModel} from '@tanstack/react-table';
import { sortMonths } from "../utils";
import { usePurchaseNeeds } from "../scripts/queries/usePurchaseNeedsQueries";
import { useAllForecasts, useForecastJSON } from "../scripts/queries/useForecastQueries";
import { useSaveToStorage, loadFromStorage } from "../utils";
import exportToCSV from "../utils/ExportExcel";

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];

export const useTableLogic = () => {
    const persisted = loadFromStorage('MRP_FILTERS') || {};

    const [data, setData] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [detectedMonths, setDetectedMonths] = useState([]);
    const [search, setSearch] = useState(persisted.search || "");
    const [sorting, setSorting] = useState(persisted.sorting || []);
    const [hideZeros, setHideZeros] = useState(persisted.hideZeros || false);
    const [activeOption, setActiveOption] = useState(persisted.activeOption || '');
    const [balanceFilter, setBalanceFilter] = useState(persisted.balanceFilter || 0);
    const [selectedMaterial, setSelectedMaterial] = useState(persisted.selectedMaterial || []);
    const [selectedMonths, setSelectedMonths] = useState(persisted.selectedMonths || []);

    const {data: needs = EMPTY_ARRAY, refetch: fetchNeeds, isFetching, isLoading: loading, dataUpdatedAt} = usePurchaseNeeds();
    const {data: json = null} = useForecastJSON(activeOption?.prefix, "Materiales", !!activeOption);
    const {data: forecasts = EMPTY_ARRAY} = useAllForecasts();

    useEffect(() => {
        if (forecasts.length > 0 && activeOption && typeof activeOption === 'object') {
            const match = forecasts.find(f => f.name === activeOption.name);
            if (match) {
                setActiveOption(match);
            }
        }
    }, [forecasts.length]);

    useEffect(() => {
        if (dataUpdatedAt) {
            setLastUpdate(new Date(dataUpdatedAt));
        }
    }, [dataUpdatedAt]);

    useSaveToStorage('MRP_FILTERS', {sorting, hideZeros, activeOption, balanceFilter, selectedMaterial, search, selectedMonths});

    const totalItemForecast = (json, selectedMonths) => {
        const data = {};
        // console.log("JSON: ", json)
        
        if(json !== null && json.length > 0) {
            json.forEach(item => {
                if(item && Array.isArray(item.detalles) && item.detalles.length > 0) {
                    item.detalles.forEach(detalle => {
                        //Keyed by CodItem: different CodItems can share the exact same description (e.g. roll widths)
                        if(detalle && detalle.codItem && selectedMonths.includes(detalle.mes)) {
                            const codItem = detalle.codItem;
                            const itemForecast = detalle.forecast;
                            const mes = detalle.mes;
                            if(!data[codItem]) {
                                data[codItem] = {};
                            }

                            const currentForecast = data[codItem][mes] ?? 0;
                            data[codItem][mes] = currentForecast + itemForecast;
                        }
                    });
                }
            });
        }

        // console.log("Item forecast: ", data);
        return data;
    }

    useEffect(() => {
        if(needs && needs.length > 0) {
            console.log("Pantone components in needs: ", needs.filter(it => it.isPantoneComponente));

            const descForecast = totalItemForecast(json, selectedMonths);
            const materials = new Set();
            // console.log("Descforecast: ", descForecast);

            //Only CodItems enrolled in catalogo_link (isUnifiable) that share a (normalized) description aggregate into a single row;
            //anything else keeps its own row, matching descriptions alone must never unify items that weren't registered for it
            const groups = {};
            needs.forEach(item => {
                const descKey = item.isUnifiable ? (item.Descricao || "").toLowerCase().trim() : `solo:${item.CodItem}`;
                if(!groups[descKey]) {
                    groups[descKey] = [];
                }
                groups[descKey].push(item);
                materials.add(item.TipoMaterial);
            });

            const processData = Object.values(groups).map(items => {
                let suggestion;
                const first = items[0];     //The first CodItem found represents the whole aggregation

                //Sum the stock quantities across every CodItem with this description
                const balance = items.reduce((acc, it) => acc + (Number(it.SaldoEstoque) || 0), 0);
                const pawn = items.reduce((acc, it) => acc + (Number(it.Qtde_Empenhada) || 0), 0);
                const order = items.reduce((acc, it) => acc + (Number(it.Qtd_Ped) || 0), 0);
                const futureBalance = items.reduce((acc, it) => acc + (Number(it.SaldoFuturo) || 0), 0);

                //Min/Max are NOT summed: keep the highest value found across the CodItems
                const minimum = items.reduce((mx, it) => Math.max(mx, Number(it.EstoqueMinimo_Considerado) || 0), 0);
                const maximum = items.reduce((mx, it) => Math.max(mx, Number(it.EstoqueMaximoReal) || 0), 0);

                //Componentes don't have their own forecast entry: sum each distinct parent pantone's forecast, scaled by its own porcentaje.
                //Any other row sums the forecast of each of its CodItems (just one unless it's unified)
                const isComponentGroup = items.some(it => it.isPantoneComponente);
                const parentContributions = isComponentGroup
                    ? Object.values(items.filter(it => it.isPantoneComponente).reduce((acc, it) => {
                        const parentKey = it.parentCodItem;
                        if(!acc[parentKey]) {
                            acc[parentKey] = {forecastKey: parentKey, porcentaje: Number(it.porcentaje) || 0};
                        }
                        return acc;
                    }, {}))
                    : [...new Set(items.map(it => it.CodItem))].map(codItem => ({forecastKey: codItem, porcentaje: 1}));

                let totalForecast = 0;
                const cleanData = {};

                if (selectedMonths.length > 0) {
                    selectedMonths.forEach(month => {
                        const numVal = parentContributions.reduce((acc, {forecastKey, porcentaje}) => {
                            const val = (descForecast[forecastKey] || {})[month];
                            return acc + ((val !== undefined && val !== null) ? Number(val) * porcentaje : 0);
                        }, 0);
                        //Add every forecast value, according to the selected months
                        totalForecast += numVal;
                        cleanData[month] = numVal;
                    })
                }

                //The forecast of the row's CodItems is subtracted from their summed balance
                const netBalance = futureBalance - totalForecast;

                let priority = 3;   //Default
                if (netBalance < 0) {
                    priority = 1;   //Red
                } else if (netBalance < minimum && netBalance > 0) {
                    priority = 2;   //Orange
                }

                //Calculate 'suggestion' based on future balance, minimum, and maximum stock levels
                if (netBalance > 0 || netBalance > minimum) {
                    suggestion = 0;
                } else {
                    if (maximum && maximum !== 0.000) {
                        suggestion = maximum - netBalance;
                    } else if (minimum && minimum !== 0.000) {
                        suggestion = minimum - netBalance;
                    } else {
                        suggestion = netBalance * -1;
                    }
                }

                suggestion = Math.max(0, suggestion);

                //Values need to match the name of the table columns
                return {
                    ...cleanData,
                    priority: priority,
                    code: first.CodItem,    //First CodItem found represents the aggregation
                    allCodes: items.map(it => it.CodItem).filter(Boolean).join(', '),   //All merged codes, kept so search still finds any of them
                    provider: (first.provider || "").split(', ')[0],  //Only the first of the CodItem's providers is shown
                    allProviders: first.provider,   //All providers, kept so search still finds any of them
                    description: first.Descricao,
                    materialType: first.TipoMaterial,
                    suggestion: suggestion,
                    unit: first.UnidadeEstoque,
                    balance: balance,
                    pawn: pawn,
                    order: order,
                    futureBalance: netBalance,  //Show the net balance instead of the original value
                    minimum: minimum,
                    maximum: maximum,
                    aggregatedCount: items.length,
                    isAggregated: items.length > 1,     //Drives the blue description in the table
                    isPantoneComponente: isComponentGroup,  //Drives the green code in the table
                };
            });

            console.log("Pantone components in processData: ", processData.filter(it => it.isPantoneComponente));

            setData(processData);
            //Prepare the data for the dropdown
            setMaterialTypes(Array.from(materials).sort());
        }
    }, [needs, json, selectedMonths]);

    useEffect(() => {
        setIsFiltering(true);
        const processFilter = setTimeout(() => {
            try {
                let result = [...data];

                if (selectedMaterial.length > 0) {
                    const selected = selectedMaterial.map(s => s.toLowerCase().trim());

                    result = result.filter(item => selected.includes(item.materialType?.toLowerCase().trim()))
                }
                
                if(search) {
                    const lowerSearch = search.toLowerCase();

                    //Filter any value that includes the search term
                    result = result.filter(item => item.code?.toLowerCase().includes(lowerSearch) || item.allCodes?.toLowerCase().includes(lowerSearch) || item.description?.toLowerCase().includes(lowerSearch) || (item.allProviders?.toLowerCase().includes(lowerSearch) && selectedMonths.length > 0));
                    // console.log("Search filter: ", searchFilter);
                }

                if (hideZeros) {
                    result = result.filter(item => {
                        return item.futureBalance < 0 || item.futureBalance >= 0.001 //This avoids exporting data that isn't shown in the app
                    });
                }

                setFilteredData(result);
            } catch(error) {
                console.error("Failed to filter data: ", error);
            } finally {
                setIsFiltering(false);
            }
        }, 0);

        return () => clearTimeout(processFilter);
    }, [search, data, hideZeros, selectedMaterial, balanceFilter]);
    
    const formatForecastName = (forecast) => {
        const unsortedMonths = forecast.uniqueMonths;

        if (!Array.isArray(unsortedMonths) || unsortedMonths.length === 0 || !forecast.name.includes("Forecast: on")) {
            return forecast.name;
        }
        
        const sortedMonths = sortMonths(unsortedMonths);
        // console.log("Sorted Months: ", sortedMonths);
        
        const firstMonth = sortedMonths[0];
        const lastMonth = sortedMonths[sortedMonths.length - 1];
        const rangeName = `${firstMonth} - ${lastMonth}`

        return rangeName
    }

    useEffect(() => {
        try {
            if(json) {
                const monthsSet = new Set();
                const forecastArr = (json && typeof json === 'object' && !Array.isArray(json)) ? Object.values(json) : (Array.isArray(json) ? json : []);

                forecastArr.forEach(item => {
                    if(item) {
                        item.detalles?.forEach(detalle => {
                            if(detalle && detalle.mes) {
                                monthsSet.add(detalle.mes);
                            }
                        })
                    }
                });

                const sortedMonths = sortMonths(Array.from(monthsSet));
                // console.log("Sorted months: ", sortedMonths);
                setDetectedMonths(sortedMonths);
            }
        } catch(error) {
            console.error("Error detecting months: ", error);
        }
        
    }, [json]);

    const customSort = (rowA, rowB, columnId) => {
        const valA = Number(rowA.getValue(columnId)) || 0;
        const valB = Number(rowB.getValue(columnId)) || 0;

        if (valA === valB) return 0;

        return valA - valB;
    }

    const generateColumns = (selectedMonths) => {
        const forecastSelected = selectedMonths.length > 0;

        const cellFormatter = (info) => {
            const value = info.getValue();
            return typeof value === 'number' ? value.toLocaleString('es-MX', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : value;
        };

        const colConfig = {
            cell: cellFormatter,
            sortingFn: customSort,
            sortUndefined: 'last'   //Force null/undefined values to be at the last positions
        }

        const baseColumns = [
            {accessorKey: 'priority', header: 'Prioridad'},
            {accessorKey: 'code', header: 'Código', width: '132px'},
            forecastSelected && {accessorKey: 'provider', header: 'Proveedor', width: '294px'},
            {accessorKey: 'description', header: 'Descripción', width: '294px'},
            !forecastSelected && {accessorKey: 'suggestion', header: 'Sugerencia', width: '120px', ...colConfig},
            {accessorKey: 'unit', header: 'Unidad', width: '30px'},
            {accessorKey: 'balance', header: 'Saldo', width: '80px', ...colConfig},
            {accessorKey: 'pawn', header: 'Empeño', width: '80px', ...colConfig},
            {accessorKey: 'order', header: 'Pedido', width: '80px', ...colConfig},
            {accessorKey: 'futureBalance', header: 'S. futuro', width: '80px', ...colConfig},
            {accessorKey: 'minimum', header: 'Mínimo', width: '80px', ...colConfig},
            {accessorKey: 'maximum', header: 'Máximo', width: '80px', ...colConfig},
        ].filter(Boolean);

        let finalColumns = [...baseColumns];

        if(forecastSelected) {
            const sortedMonths = sortMonths(selectedMonths);
            const forecastColumns = sortedMonths.map(month => ({
                accessorKey: month, header: month.split(' ')[0], width: '80px', ...colConfig
            }));

            const orderIndex = finalColumns.findIndex(col => col.accessorKey === 'order');
            finalColumns.splice(orderIndex, 0, ...forecastColumns);
        }

        return finalColumns;
    }

    const activeSorting = useMemo(() => {
        if (balanceFilter) {
            //Keep the priority and apply the selected filter
            return [{id: 'priority', desc: false}, ...sorting.filter(s => s.id !== 'priority')]
        }
        return sorting;
    }, [balanceFilter, sorting]);

    const columns = useMemo(() => 
        generateColumns(selectedMonths)
    , [selectedMonths, sorting])

    const table = useReactTable({
        data: filteredData,
        columns: columns,
        state: {
            sorting: activeSorting,
            columnVisibility: {
                priority: false //Hide this column, it's just required for the sorting
            }
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const exportCSV = () => {
        const csvRows = [];

        const exportableCols = columns.filter(col => col.accessorKey !== 'priority');
        const headers = [...exportableCols.map((col) => col.header), "Tipo de material"];
        csvRows.push(headers); //Add headers to the csv

        filteredData.forEach((row) => {
            const values = exportableCols.map((col) => {
                const accessor = col.accessorKey;
                let value = row[accessor];

                if (accessor === 'code' && row.isAggregated) {
                    value = 'Unificado';
                }

                if (value === undefined || value === null) {    //This is crucial for correct sorting
                    value = 0;
                }                
                
                return value;
            });
            const materialTypeVal = row.materialType || "";
            csvRows.push([...values, materialTypeVal]);
        });

        exportToCSV("MPR.xlsx", csvRows);
    }

    return {table, forecasts, detectedMonths, activeOption, lastUpdate, loading, hideZeros, isFiltering, materialTypes, selectedMaterial, balanceFilter, isFetching, search, selectedMonths, setActiveOption, setDetectedMonths, exportCSV, fetchNeeds, formatForecastName, setHideZeros, setSelectedMaterial, setBalanceFilter, setSearch, setSelectedMonths};
}